import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { EmailDetail, MessageView, ThreadSummary } from "@/lib/types";
import { Card, CardTitle } from "@/components/Card";
import MessageBubble, { messageStamp } from "@/components/MessageBubble";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/cn";

/**
 * How many of the newest messages are shown in full. Everything older collapses
 * to a one-line summary, because the question this page answers is "what was
 * said last, and what are we about to say" — an unrolled twenty-message history
 * pushes both of those off the screen.
 */
const OPEN_TAIL = 3;

/**
 * The conversation with one prospect: their replies, ours, and the one we are
 * about to send — opened, in order, without the operator clicking anything.
 *
 * <p>Messages run newest first. The message awaiting approval sits at the top
 * in its full reviewer, directly above the reply it answers, so approving,
 * editing or rejecting it happens in the one place where it can be judged.
 */
export default function ProspectConversation({
  threads,
  threadsLoading,
  pendingDraft,
  refreshMs,
}: {
  threads: ThreadSummary[];
  threadsLoading: boolean;
  /** The message blocked on a human, if this prospect has one. */
  pendingDraft: EmailDetail | null;
  refreshMs: number;
}) {
  // Which thread the operator asked for. Null means "whichever one matters
  // now", re-decided as mail arrives.
  const [pickedThreadId, setPickedThreadId] = useState<string | null>(null);
  const openThreadId = resolveOpenThread(threads, pickedThreadId, pendingDraft);

  const messages = useQuery({
    queryKey: ["thread-messages", openThreadId],
    queryFn: () => api.get<MessageView[]>(`/api/v1/threads/${openThreadId}/messages`),
    enabled: !!openThreadId,
    refetchInterval: refreshMs,
  });

  // Reading choices belong to the thread they were made in.
  // `undefined` means nobody has chosen yet, which is what lets the pending
  // message open itself without also forcing it back open after a collapse.
  const [openReviewId, setOpenReviewId] = useState<string | null | undefined>(undefined);
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [showEarlier, setShowEarlier] = useState(false);
  useEffect(() => {
    setOpenReviewId(undefined);
    setExpandedIds([]);
    setShowEarlier(false);
  }, [openThreadId]);

  // The message awaiting approval, held locally once seen.
  //
  // <p>It is kept for two moments when the server-side answer is briefly not
  // the useful one. Before: a message composed seconds ago is not yet in the
  // cached thread list, and that is the message the operator opened this page
  // for. After: approving it clears `pendingDraftId`, which would otherwise
  // take the message — and the confirmation of what the send actually did —
  // off the screen in the same instant the operator clicked. The thread's own
  // refresh supersedes it by id, so nothing is shown twice.
  const [pinned, setPinned] = useState<{ threadId: string; row: MessageView } | null>(null);
  useEffect(() => {
    if (pendingDraft) {
      setPinned({ threadId: pendingDraft.threadId, row: asMessageView(pendingDraft) });
    }
  }, [pendingDraft]);

  if (threadsLoading) {
    return (
      <Card>
        <CardTitle>Conversation</CardTitle>
        <div className="text-sm text-zinc-500">Loading…</div>
      </Card>
    );
  }
  if (threads.length === 0) {
    return (
      <Card>
        <CardTitle>Conversation</CardTitle>
        <div className="text-sm text-[var(--fg-muted)]">
          No messages yet. Use the controls beside this panel to put the agent to
          work, or write the first message yourself below.
        </div>
      </Card>
    );
  }

  const openThread = threads.find((t) => t.id === openThreadId) ?? null;
  const pinnedRow = pinned && pinned.threadId === openThreadId ? pinned.row : null;
  const rows = newestFirst(messages.data ?? [], pinnedRow);
  // Until the operator chooses, the message awaiting approval is the one under
  // review — whether it arrived pinned from the prospect record or the thread
  // list simply carries it. Its controls are why anyone is on this page, so
  // they are never a click away.
  const reviewingId =
    openReviewId === undefined
      ? pinnedRow?.id ?? rows.find(awaitingApproval)?.id ?? null
      : openReviewId;
  const hiddenCount = showEarlier ? 0 : Math.max(0, rows.length - OPEN_TAIL);

  return (
    <Card>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <CardTitle>Conversation</CardTitle>
        {openThread ? (
          <span className="mb-2 text-xs text-[var(--fg-muted)]">
            {openThread.messageCount} messages · last{" "}
            {formatDateTime(openThread.lastMessageAt ?? openThread.createdAt)}
          </span>
        ) : null}
      </div>

      {threads.length > 1 ? (
        <div className="mb-3 flex flex-wrap gap-2">
          {threads.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setPickedThreadId(t.id)}
              className={cn(
                "max-w-[22rem] truncate rounded-full border px-3 py-1 text-xs",
                t.id === openThreadId
                  ? "border-[var(--primary)] bg-blue-50 text-[var(--primary)]"
                  : "border-[var(--border)] hover:bg-zinc-50",
              )}
            >
              {t.subject}
            </button>
          ))}
        </div>
      ) : openThread ? (
        <div className="mb-3 text-sm font-medium">{openThread.subject}</div>
      ) : null}

      {messages.isLoading ? (
        <div className="text-sm text-zinc-500">Loading messages…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-[var(--fg-muted)]">No messages in this thread.</div>
      ) : (
        <div className="space-y-3">
          {rows.map((m, i) => {
            const collapsed = i >= OPEN_TAIL && !showEarlier && !expandedIds.includes(m.id);
            return (
              <MessageBubble
                key={m.id}
                m={m}
                state={reviewingId === m.id ? "review" : collapsed ? "collapsed" : "body"}
                onExpand={() => setExpandedIds((ids) => [...ids, m.id])}
                onToggleReview={() => setOpenReviewId(reviewingId === m.id ? null : m.id)}
              />
            );
          })}
          {hiddenCount > 0 ? (
            <button
              type="button"
              onClick={() => setShowEarlier(true)}
              className="text-xs text-[var(--primary)] hover:underline"
            >
              Show {hiddenCount} earlier message{hiddenCount === 1 ? "" : "s"} in full
            </button>
          ) : null}
        </div>
      )}
    </Card>
  );
}

/**
 * Which thread to show: the operator's choice, else the one holding the message
 * awaiting approval, else the one that moved most recently.
 *
 * <p>The pending message outranks recency because it is the only thing on this
 * page someone has to act on, and it is not always in the newest thread — a
 * prospect who starts a fresh subject line leaves the blocked reply behind in
 * the older one.
 */
function resolveOpenThread(
  threads: ThreadSummary[],
  picked: string | null,
  pendingDraft: EmailDetail | null,
): string | null {
  if (picked && threads.some((t) => t.id === picked)) return picked;
  if (pendingDraft && threads.some((t) => t.id === pendingDraft.threadId)) {
    return pendingDraft.threadId;
  }
  const newest = [...threads].sort((a, b) => activity(b).localeCompare(activity(a)))[0];
  return newest?.id ?? null;
}

function activity(t: ThreadSummary): string {
  return t.lastMessageAt ?? t.createdAt;
}

/** The thread newest-first, including a pinned message the list does not carry yet. */
function newestFirst(messages: MessageView[], pinned: MessageView | null): MessageView[] {
  const rows = [...messages];
  if (pinned && !rows.some((m) => m.id === pinned.id)) rows.push(pinned);
  return rows.sort((a, b) => messageStamp(b).localeCompare(messageStamp(a)));
}

function awaitingApproval(m: MessageView): boolean {
  return m.direction === "OUTBOUND" && m.status === "DRAFT";
}

function asMessageView(d: EmailDetail): MessageView {
  return {
    id: d.id,
    direction: d.direction,
    status: d.status,
    subject: d.subject,
    bodyText: d.bodyText,
    bodyHtml: d.bodyHtml,
    sentAt: d.sentAt,
    receivedAt: d.receivedAt,
    deliveredAt: d.deliveredAt,
    openedAt: d.openedAt,
    bounceReason: d.bounceReason,
    createdAt: d.createdAt,
    injectionSuspected: d.injectionSuspected,
  };
}
